import { LayoutDashboard, MessageSquare, AlertTriangle, BarChart2, Settings, LogOut, MapPin, Plus } from 'lucide-react'
import type { ActivePage } from '../../types/dashboard'

interface SidebarLocation {
  id: string
  name: string
}

interface DashboardSidebarProps {
  businessName: string
  plan: string
  maxLocations: number
  locations: SidebarLocation[]
  selectedLocationId: string
  onLocationChange: (id: string) => void
  onAddLocation: () => void
  activePage: ActivePage
  onPageChange: (page: ActivePage) => void
  escalatedCount: number
  inboxCount: number
  onLogout: () => void
}

export default function DashboardSidebar({
  businessName,
  plan,
  maxLocations,
  locations,
  selectedLocationId,
  onLocationChange,
  onAddLocation,
  activePage,
  onPageChange,
  escalatedCount,
  inboxCount,
  onLogout,
}: DashboardSidebarProps) {
  const canAddLocation = plan !== 'starter'
  const atLocationLimit = maxLocations !== -1 && locations.length >= maxLocations
  const navItems = [
    {
      id: 'dashboard' as ActivePage,
      label: 'Dashboard',
      icon: <LayoutDashboard className="w-4 h-4" />,
      badge: null,
    },
    {
      id: 'settings' as ActivePage,
      label: 'Settings',
      icon: <Settings className="w-4 h-4" />,
      badge: null,
    },
  ]

  return (
    <aside className="w-52 flex-shrink-0 bg-rd-surface border-r border-white/[0.08] flex flex-col py-4 gap-5 overflow-y-auto">

      {/* Business card */}
      <div className="px-3">
        <div className="bg-rd-surface2 border border-white/[0.08] rounded-xl p-3">
          <p className="text-xs font-medium text-rd-text truncate mb-0.5">{businessName}</p>

          {locations.length > 1 ? (
            <div className="flex items-center gap-1.5 bg-rd-surface border border-white/[0.08] rounded-lg px-2 py-1.5 mt-2">
              <MapPin className="w-2.5 h-2.5 text-rd-muted flex-shrink-0" />
              <select
                value={selectedLocationId}
                onChange={(e) => onLocationChange(e.target.value)}
                className="bg-transparent border-none outline-none text-rd-muted text-[11px] cursor-pointer w-full"
              >
                {locations.map((loc) => (
                  <option key={loc.id} value={loc.id} style={{ background: '#1a1a26' }}>
                    {loc.name}
                  </option>
                ))}
              </select>
            </div>
          ) : locations.length === 1 ? (
            <p className="text-[11px] text-rd-dim mt-1 flex items-center gap-1">
              <MapPin className="w-2.5 h-2.5" />
              {locations[0].name}
            </p>
          ) : null}

          {canAddLocation && (
            <button
              type="button"
              onClick={onAddLocation}
              disabled={atLocationLimit}
              title={atLocationLimit ? `Location limit reached (${maxLocations}). Upgrade your plan to add more.` : undefined}
              className={`flex items-center gap-1.5 mt-2 text-[11px] transition-colors ${
                atLocationLimit
                  ? 'text-rd-dim cursor-not-allowed'
                  : 'text-rd-accent hover:text-rd-text'
              }`}
            >
              <Plus className="w-3 h-3" />
              Add location
            </button>
          )}
        </div>
      </div>

      {/* Nav */}
      <div className="px-3 flex-1">
        <p className="text-[10px] text-rd-dim uppercase tracking-widest px-1 mb-2">Menu</p>
        <div className="flex flex-col gap-0.5">
          {navItems.map((item) => (
            <button
              key={item.id}
              type="button"
              onClick={() => onPageChange(item.id)}
              className={`flex items-center gap-2.5 px-2.5 py-2 rounded-lg text-[13px] transition-colors w-full text-left ${
                activePage === item.id
                  ? 'bg-rd-accent/10 text-rd-accent'
                  : 'text-rd-muted hover:bg-rd-surface2 hover:text-rd-text'
              }`}
            >
              {item.icon}
              {item.label}
            </button>
          ))}

          {/* Inbox */}
          <button
            type="button"
            onClick={() => onPageChange('inbox')}
            className={`flex items-center gap-2.5 px-2.5 py-2 rounded-lg text-[13px] transition-colors w-full text-left ${
              activePage === 'inbox'
                ? 'bg-rd-accent/10 text-rd-accent'
                : 'text-rd-muted hover:bg-rd-surface2 hover:text-rd-text'
            }`}
          >
            <MessageSquare className="w-4 h-4" />
            Inbox
            {inboxCount > 0 && (
              <span className="ml-auto text-[10px] font-medium px-1.5 py-0.5 rounded-full bg-warning-500/15 text-warning-500">
                {inboxCount}
              </span>
            )}
          </button>

          {/* Escalations */}
          <button
            type="button"
            onClick={() => onPageChange('escalations')}
            className={`flex items-center gap-2.5 px-2.5 py-2 rounded-lg text-[13px] transition-colors w-full text-left ${
              activePage === 'escalations'
                ? 'bg-rd-accent/10 text-rd-accent'
                : 'text-rd-muted hover:bg-rd-surface2 hover:text-rd-text'
            }`}
          >
            <AlertTriangle className="w-4 h-4" />
            Escalations
            {escalatedCount > 0 && (
              <span className="ml-auto text-[10px] font-medium px-1.5 py-0.5 rounded-full bg-danger-500/15 text-danger-500">
                {escalatedCount}
              </span>
            )}
          </button>

          {/* Analytics */}
          <button
            type="button"
            onClick={() => onPageChange('analytics')}
            className={`flex items-center gap-2.5 px-2.5 py-2 rounded-lg text-[13px] transition-colors w-full text-left ${
              activePage === 'analytics'
                ? 'bg-rd-accent/10 text-rd-accent'
                : 'text-rd-muted hover:bg-rd-surface2 hover:text-rd-text'
            }`}
          >
            <BarChart2 className="w-4 h-4" />
            Analytics
          </button>
        </div>
      </div>

      {/* Logout */}
      <div className="px-3">
        <button
          type="button"
          onClick={onLogout}
          className="flex items-center gap-2 px-2.5 py-2 w-full rounded-lg text-[13px] text-rd-dim hover:text-rd-muted hover:bg-rd-surface2 transition-colors"
        >
          <LogOut className="w-4 h-4" />
          Log out
        </button>
      </div>
    </aside>
  )
}
